import PropTypes from "prop-types";
import { useEffect, useRef, useState, useMemo } from "react";
import Webcam from "react-webcam";
import {
  saveCameraPreferences,
  isPreferredCamera,
  validateCameraPreference,
} from "../../../../util/cameraPreferences";
import "../styles.css";

const WebcamPopup = ({
  setShowWebcamPopup,
  setCapturedImage,
  addImage,
  addVideo,
  showErrorSnackbar,
}) => {
  const webcamRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const fileInputRef = useRef(null);

  const [captured, setCaptured] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFileInput, setShowFileInput] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [capturedVideo, setCapturedVideo] = useState(null);

  // Camera zoom and focus state
  const [zoomLevel, setZoomLevel] = useState(1); // Start at 1x (normal zoom)
  const [cameraZoomLevel, setCameraZoomLevel] = useState(1); // For camera constraints (optical zoom)
  const [uiZoomLevel, setUiZoomLevel] = useState(1); // For CSS transform (when optical zoom not supported)
  const [focusMode, setFocusMode] = useState("auto"); // 'auto' or 'manual'
  const [focusDistance, setFocusDistance] = useState(0);
  const [cameraCapabilities, setCameraCapabilities] = useState({
    zoom: { min: 1, max: 1, step: 0.1 },
    focusMode: [],
    focusDistance: { min: 0, max: 1, step: 0.01 },
    exposureMode: [],
    exposureCompensation: { min: -2, max: 2, step: 0.1 },
    whiteBalanceMode: [],
    torch: false, // Flash/torch support
  });
  const [flashEnabled, setFlashEnabled] = useState(false);
  const videoTrackRef = useRef(null);
  const cameraFeedRef = useRef(null); // Ref for the video element to apply CSS zoom

  // Tap-to-focus state
  const [focusPoint, setFocusPoint] = useState(null); // { x, y } in normalized coordinates (0-1)
  const [showFocusIndicator, setShowFocusIndicator] = useState(false);
  const focusIndicatorTimeoutRef = useRef(null);
  const liveCameraRef = useRef(null);

  // Zoom presets (like smartphone camera apps)
  const zoomPresets = [1, 1.5, 2, 3, 5];

  // Helper function to find and apply zoom to video element
  const applyZoomToVideoElement = (zoomValue) => {
    // Try to find video element using multiple methods
    let videoEl = cameraFeedRef.current;

    if (!videoEl) {
      // Method 1: Try webcamRef
      if (webcamRef.current && webcamRef.current.video) {
        videoEl = webcamRef.current.video;
        cameraFeedRef.current = videoEl;
      }
      // Method 2: Try querySelector
      if (!videoEl) {
        const wrapper = document.querySelector(".camera-feed-wrapper");
        if (wrapper) {
          videoEl = wrapper.querySelector("video");
          if (videoEl) {
            cameraFeedRef.current = videoEl;
          }
        }
      }
      // Method 3: Try direct query
      if (!videoEl) {
        videoEl =
          document.querySelector(".webcam-feed video") ||
          document.querySelector(".camera-feed-wrapper video");
        if (videoEl) {
          cameraFeedRef.current = videoEl;
        }
      }
    }

    if (!videoEl) {
      console.warn(
        "[WEBCAM DEBUG] ⚠️ Video element not found - cannot apply zoom"
      );
      return false;
    }

    // Only apply CSS zoom if camera doesn't support optical zoom
    const useCssZoom = cameraCapabilities.zoom.max <= 1;

    if (useCssZoom) {
      // Apply CSS transform - use both methods for maximum compatibility
      const transformValue = `scale(${zoomValue})`;

      // Direct style assignment (highest priority)
      videoEl.style.cssText += `; transform: ${transformValue} !important; -webkit-transform: ${transformValue} !important; transform-origin: center center !important;`;

      // Also use setProperty
      videoEl.style.setProperty("transform", transformValue, "important");
      videoEl.style.setProperty(
        "-webkit-transform",
        transformValue,
        "important"
      );
      videoEl.style.setProperty(
        "transform-origin",
        "center center",
        "important"
      );


      // Verify it was applied
      const computed = window.getComputedStyle(videoEl);

      return true;
    } else {
      // Clear CSS transform when using optical zoom
      videoEl.style.transform = "none";
      videoEl.style.webkitTransform = "none";
      return true;
    }
  };

  // Apply CSS zoom transform when uiZoomLevel changes (for cameras without optical zoom)
  useEffect(() => {
    if (cameraCapabilities.zoom.max <= 1) {
      applyZoomToVideoElement(uiZoomLevel);
    } else {
      applyZoomToVideoElement(1); // Clear zoom for optical zoom cameras
    }
  }, [uiZoomLevel, cameraCapabilities.zoom.max]);

  // Pinch-to-zoom state
  const pinchStateRef = useRef({
    initialDistance: 0,
    initialZoom: 1,
    isPinching: false,
  });

  // Trackpad zoom state (for Mac)
  const trackpadZoomRef = useRef({
    lastWheelTime: 0,
    accumulatedZoom: 1,
  });

  const getDeviceLabel = (device, index) => {
    const rawLabel = device?.label?.trim();
    if (!rawLabel) {
      return `Camera ${index + 1}`;
    }

    const isObfuscatedLabel =
      /^[0-9a-fA-F:-]+$/.test(rawLabel) || rawLabel.toLowerCase() === "default";

    return isObfuscatedLabel ? `Camera ${index + 1}` : rawLabel;
  };

  useEffect(() => {
    if (!permissionGranted || devices.length === 0) {
      return;
    }

    if (!selectedDeviceId) {
      const validation = validateCameraPreference(devices);
      const deviceToSelect = validation.isValid
        ? validation.deviceId
        : devices[0].deviceId;

      setSelectedDeviceId(deviceToSelect);
    }
  }, [devices, permissionGranted, selectedDeviceId]);

  // Handle camera device selection change
  const handleDeviceChange = (newDeviceId) => {
    setSelectedDeviceId(newDeviceId);

    // Find the selected device to get its label
    const selectedDevice = devices.find(
      (device) => device.deviceId === newDeviceId
    );
    const deviceIndex = devices.findIndex(
      (device) => device.deviceId === newDeviceId
    );
    const deviceLabel = getDeviceLabel(selectedDevice, deviceIndex);

    // Save the preference
    saveCameraPreferences(newDeviceId, deviceLabel);

    // Reset zoom and focus when switching devices
    setZoomLevel(1);
    setFocusMode("auto");
    setFocusDistance(0);
    videoTrackRef.current = null;
  };

  // Handle when webcam stream is ready
  const handleUserMedia = (stream) => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrackRef.current = videoTrack;

        // Get the video element reference
        if (webcamRef.current && webcamRef.current.video) {
          cameraFeedRef.current = webcamRef.current.video;

          // Apply current zoom if UI zoom is set and camera doesn't support optical zoom
          if (cameraCapabilities.zoom.max <= 1 && uiZoomLevel !== 1) {
            const videoEl = cameraFeedRef.current;
            videoEl.style.setProperty(
              "transform",
              `scale(${uiZoomLevel})`,
              "important"
            );
            videoEl.style.setProperty(
              "transform-origin",
              "center center",
              "important"
            );
            videoEl.style.setProperty(
              "-webkit-transform",
              `scale(${uiZoomLevel})`,
              "important"
            );
            videoEl.style.setProperty("will-change", "transform", "important");
          }
        } else {
        }

        // Get capabilities from the actual track
        const capabilities = videoTrack.getCapabilities();
        const settings = videoTrack.getSettings();

        // Log capabilities for debugging

        const newCapabilities = {
          zoom: capabilities.zoom
            ? {
                min: capabilities.zoom.min || 0.5,
                max: capabilities.zoom.max || 1,
                step: capabilities.zoom.step || 0.1,
              }
            : { min: 0.5, max: 1, step: 0.1 },
          focusMode: capabilities.focusMode || [],
          focusDistance: capabilities.focusDistance
            ? {
                min: capabilities.focusDistance.min || 0,
                max: capabilities.focusDistance.max || 1,
                step: capabilities.focusDistance.step || 0.01,
              }
            : { min: 0, max: 1, step: 0.01 },
          exposureMode: capabilities.exposureMode || [],
          exposureCompensation: capabilities.exposureCompensation
            ? {
                min: capabilities.exposureCompensation.min || -2,
                max: capabilities.exposureCompensation.max || 2,
                step: capabilities.exposureCompensation.step || 0.1,
              }
            : { min: -2, max: 2, step: 0.1 },
          whiteBalanceMode: capabilities.whiteBalanceMode || [],
          colorTemperature: capabilities.colorTemperature
            ? {
                min: capabilities.colorTemperature.min || 2000,
                max: capabilities.colorTemperature.max || 8000,
                step: capabilities.colorTemperature.step || 100,
              }
            : null,
          torch: capabilities.torch !== undefined ? capabilities.torch : false, // Check if device supports torch/flash
        };

        setCameraCapabilities(newCapabilities);

        // Log zoom support status
        if (newCapabilities.zoom.max > 1) {
        } else {
          console.warn(
            "Camera does not support optical zoom. Only digital zoom (cropping) may be available."
          );
        }

        // Log torch/flash support status
        if (newCapabilities.torch) {
        } else {
        }

        // Set initial zoom from current settings if available
        if (settings.zoom !== undefined) {
          setZoomLevel(settings.zoom);
          if (newCapabilities.zoom.max > 1) {
            setCameraZoomLevel(settings.zoom);
            setUiZoomLevel(1);
          } else {
            setCameraZoomLevel(1);
            setUiZoomLevel(settings.zoom);
          }
        } else {
          setZoomLevel(newCapabilities.zoom.min);
          setCameraZoomLevel(newCapabilities.zoom.min);
          setUiZoomLevel(1);
        }

        // Set initial focus mode and distance if available
        if (settings.focusMode) {
          setFocusMode(settings.focusMode);
        }
        if (settings.focusDistance !== undefined) {
          setFocusDistance(settings.focusDistance);
        }
      }
    }
  };

  // Apply zoom constraint to camera - ONLY zoom the video element, not the UI
  const applyZoom = async (newZoom) => {
    const minZoom = cameraCapabilities.zoom.min || 0.5;
    const maxZoom =
      cameraCapabilities.zoom.max > 1 ? cameraCapabilities.zoom.max : 5;
    const clampedZoom = Math.max(minZoom, Math.min(newZoom, maxZoom));
    setZoomLevel(clampedZoom);

    if (videoTrackRef.current && cameraCapabilities.zoom.max > 1) {
      // Camera supports optical zoom - use MediaStream constraints (best quality)
      const capabilities = videoTrackRef.current.getCapabilities();

      if ("zoom" in capabilities && capabilities.zoom) {
        try {
          await videoTrackRef.current.applyConstraints({
            advanced: [{ zoom: clampedZoom }],
          });
          setCameraZoomLevel(clampedZoom);
          setUiZoomLevel(1); // Reset CSS zoom when using optical

          // Ensure video element has no CSS transform (using helper)
          applyZoomToVideoElement(1);
        } catch (err) {
          console.error(
            "[WEBCAM DEBUG] Failed to apply optical zoom, falling back to CSS:",
            err
          );
          // Fallback to CSS zoom
          setCameraZoomLevel(1);
          setUiZoomLevel(clampedZoom);
          // Apply CSS zoom fallback
          applyZoomToVideoElement(clampedZoom);
        }
      }
    } else {
      // Camera doesn't support optical zoom - use CSS transform on video element ONLY
      setCameraZoomLevel(1);
      setUiZoomLevel(clampedZoom);

      // Apply CSS transform ONLY to the video element using helper function
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        applyZoomToVideoElement(clampedZoom);
      }, 0);
    }
  };

  // Apply focus constraint to camera
  const applyFocus = async (mode, distance = null) => {
    if (videoTrackRef.current) {
      const capabilities = videoTrackRef.current.getCapabilities();
      const constraints = { advanced: [] };

      if (capabilities.focusMode && capabilities.focusMode.includes(mode)) {
        constraints.advanced.push({ focusMode: mode });
        setFocusMode(mode);

        if (mode === "manual" && distance !== null) {
          const clampedDistance = Math.max(
            cameraCapabilities.focusDistance.min,
            Math.min(distance, cameraCapabilities.focusDistance.max)
          );
          constraints.advanced.push({ focusDistance: clampedDistance });
          setFocusDistance(clampedDistance);
        }
      }

      if (constraints.advanced.length > 0) {
        try {
          await videoTrackRef.current.applyConstraints(constraints);
        } catch (err) {
          console.warn("Failed to apply focus:", err);
        }
      }
    }
  };

  // Handle tap-to-focus - Android camera style
  const handleTapToFocus = async (e) => {

    if (!videoTrackRef.current || !liveCameraRef.current) {
      return;
    }

    const rect = liveCameraRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Normalize coordinates (0-1) - Android cameras use normalized coordinates
    const normalizedX = Math.max(0, Math.min(1, x / rect.width));
    const normalizedY = Math.max(0, Math.min(1, y / rect.height));


    // Update focus point immediately for visual feedback (Android style)
    setFocusPoint({ x: normalizedX, y: normalizedY });
    setShowFocusIndicator(true);

    // Hide indicator after 3 seconds (Android style - longer visibility)
    if (focusIndicatorTimeoutRef.current) {
      clearTimeout(focusIndicatorTimeoutRef.current);
    }
    focusIndicatorTimeoutRef.current = setTimeout(() => {
      setShowFocusIndicator(false);
    }, 3000);

    // Android-style focus animation: expand then contract
    const focusIndicator = document.querySelector('.focus-indicator');
    if (focusIndicator) {
      focusIndicator.classList.remove('focusing', 'focused');
      // Trigger reflow
      void focusIndicator.offsetWidth;
      // Start focusing animation
      focusIndicator.classList.add('focusing');
      
      // After focusing animation, show focused state
      setTimeout(() => {
        focusIndicator.classList.remove('focusing');
        focusIndicator.classList.add('focused');
      }, 600);
    }

    try {
      const track = videoTrackRef.current;
      const capabilities = track.getCapabilities();
      const settings = track.getSettings();
      

      const constraints = { advanced: [] };
      let hasFocusControl = false;

      // Method 1: Try pointsOfInterest (most Android-like, works on mobile cameras)
      if (capabilities.pointsOfInterest) {
        constraints.advanced.push({
          pointsOfInterest: [{ x: normalizedX, y: normalizedY }],
        });
        hasFocusControl = true;
      }

      // Method 2: Try focusMode with auto (triggers auto-focus at point)
      if (capabilities.focusMode) {
        // First, try to trigger a focus cycle by switching modes
        if (capabilities.focusMode.includes("single-shot")) {
          constraints.advanced.push({ focusMode: "single-shot" });
          hasFocusControl = true;
        } else if (capabilities.focusMode.includes("auto")) {
          // Trigger auto-focus by briefly switching to manual then back to auto
          constraints.advanced.push({ focusMode: "auto" });
          hasFocusControl = true;
        }
      }

      // Method 3: Set exposure point (often works when focus doesn't)
      if (capabilities.exposureMode && capabilities.exposureMode.includes("auto")) {
        constraints.advanced.push({ exposureMode: "auto" });
      }

      // Apply constraints if we have any
      if (constraints.advanced.length > 0) {
        try {
          await track.applyConstraints(constraints);
          
          // For cameras that support it, try to trigger a focus cycle
          // by briefly applying manual focus then switching back
          if (capabilities.focusMode && capabilities.focusMode.includes("manual") && 
              capabilities.focusMode.includes("auto")) {
            // Trigger focus by briefly going to manual then auto
            setTimeout(async () => {
              try {
                await track.applyConstraints({
                  advanced: [{ focusMode: "manual" }]
                });
                await new Promise(resolve => setTimeout(resolve, 50));
                await track.applyConstraints({
                  advanced: [{ focusMode: "auto" }]
                });
              } catch (err) {
              }
            }, 100);
          }
        } catch (err) {
          console.warn("[TAP-TO-FOCUS] ⚠️ Failed to apply constraints:", err);
        }
      }

      // Store focus point for potential post-processing if camera doesn't support hardware focus
      if (!hasFocusControl) {
      }

    } catch (err) {
      console.warn("[TAP-TO-FOCUS] ❌ Error during tap-to-focus:", err);
    }

  };


  // Handle zoom preset selection
  const handleZoomPreset = (presetZoom) => {
    applyZoom(presetZoom);
  };

  // Handle flash/torch toggle
  const toggleFlash = async () => {
    if (!videoTrackRef.current || !cameraCapabilities.torch) return;

    const newFlashState = !flashEnabled;
    setFlashEnabled(newFlashState);

    try {
      await videoTrackRef.current.applyConstraints({
        advanced: [{ torch: newFlashState }],
      });
    } catch (err) {
      console.warn("[WEBCAM DEBUG] Failed to toggle flash:", err);
      // Revert state on error
      setFlashEnabled(!newFlashState);
    }
  };

  // Handle zoom change
  const handleZoomChange = (e) => {
    const newZoom = parseFloat(e.target.value);
    applyZoom(newZoom);
  };

  // Handle focus mode change
  const handleFocusModeChange = (e) => {
    const newMode = e.target.value;
    if (newMode === "manual") {
      applyFocus("manual", focusDistance);
    } else {
      applyFocus("auto");
    }
  };

  // Handle focus distance change (for manual focus)
  const handleFocusDistanceChange = (e) => {
    const newDistance = parseFloat(e.target.value);
    applyFocus("manual", newDistance);
  };

  // Calculate distance between two touch points
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start for pinch-to-zoom
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);

      // Use appropriate zoom level based on camera support
      const currentZoom =
        cameraCapabilities.zoom.max > 1 ? zoomLevel : uiZoomLevel;

      pinchStateRef.current = {
        initialDistance: distance,
        initialZoom: currentZoom,
        isPinching: true,
      };

      // Stop propagation to prevent canvas handlers from intercepting
      e.stopPropagation();
    }
  };

  // Handle touch move for pinch-to-zoom
  const handleTouchMove = (e) => {
    if (pinchStateRef.current.isPinching && e.touches.length === 2) {
      e.preventDefault(); // Prevent default pinch behavior
      e.stopPropagation(); // Stop propagation to prevent canvas handlers from intercepting

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = getTouchDistance(touch1, touch2);

      if (pinchStateRef.current.initialDistance > 0) {
        // Calculate zoom factor based on distance change
        const zoomFactor =
          currentDistance / pinchStateRef.current.initialDistance;
        const currentZoom =
          cameraCapabilities.zoom.max > 1 ? zoomLevel : uiZoomLevel;
        const newZoom = pinchStateRef.current.initialZoom * zoomFactor;

        // Clamp zoom level - use camera max if supported, otherwise max 5x for CSS zoom
        const maxZoom =
          cameraCapabilities.zoom.max > 1 ? cameraCapabilities.zoom.max : 5;
        const clampedZoom = Math.max(1, Math.min(newZoom, maxZoom));

        // Apply zoom to camera (will use optical or CSS depending on support)
        applyZoom(clampedZoom);
      }
    }
  };

  // Handle touch end for pinch-to-zoom
  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchStateRef.current.isPinching = false;
      pinchStateRef.current.initialDistance = 0;
      // Stop propagation to prevent canvas handlers from intercepting
      e.stopPropagation();
    }
  };

  // Handle touch cancel for pinch-to-zoom
  const handleTouchCancel = (e) => {
    pinchStateRef.current.isPinching = false;
    pinchStateRef.current.initialDistance = 0;
    // Stop propagation to prevent canvas handlers from intercepting
    e.stopPropagation();
  };

  // Handle wheel events for Mac trackpad pinch-to-zoom
  const handleWheel = (e) => {
    // Check if this is a pinch gesture (Ctrl/Cmd + wheel or trackpad pinch)
    const isPinchGesture =
      e.ctrlKey ||
      e.metaKey ||
      (e.deltaMode === 0 &&
        Math.abs(e.deltaY) < 100 &&
        Math.abs(e.deltaX) < 100);

    // Only handle if the event is directly on the camera preview area
    const target = e.target;
    const isOnCameraPreview =
      target &&
      (target.closest(".live-camera") ||
        target.closest(".webcam-feed") ||
        target.closest(".camera-feed-wrapper") ||
        target.classList.contains("live-camera") ||
        target.classList.contains("webcam-feed") ||
        target.classList.contains("camera-feed-wrapper") ||
        (target.tagName === "VIDEO" && target.closest(".live-camera")));

    // If it's a pinch gesture, ALWAYS prevent browser zoom first
    if (isPinchGesture) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }

      // Immediately reset any browser zoom that might have been applied
      const resetBrowserZoom = () => {
        document.body.style.zoom = "1";
        document.documentElement.style.zoom = "1";
        document.body.style.transform = "scale(1)";
        document.documentElement.style.transform = "scale(1)";
        document.body.style.scale = "1";
        document.documentElement.style.scale = "1";

        const overlayEl = document.querySelector(".webcam-overlay");
        const contentEl = document.querySelector(".webcam-content");
        if (overlayEl) {
          overlayEl.style.zoom = "1";
          overlayEl.style.transform = "scale(1)";
        }
        if (contentEl) {
          contentEl.style.zoom = "1";
          contentEl.style.transform = "scale(1)";
        }
      };

      resetBrowserZoom();

      // Only apply camera zoom if we're on the camera preview
      if (isOnCameraPreview) {
        // Determine zoom direction and amount
        // On Mac trackpad: negative deltaY = zoom in, positive = zoom out
        const zoomDelta = e.deltaY;
        const zoomFactor = zoomDelta > 0 ? 0.98 : 1.02;
        const currentTime = Date.now();

        // Use appropriate zoom level based on camera support
        const currentZoom =
          cameraCapabilities.zoom.max > 1 ? zoomLevel : uiZoomLevel;

        // Reset accumulated zoom if too much time has passed (new gesture)
        if (currentTime - trackpadZoomRef.current.lastWheelTime > 150) {
          trackpadZoomRef.current.accumulatedZoom = currentZoom;
        }

        // Apply zoom factor
        const newZoom = trackpadZoomRef.current.accumulatedZoom * zoomFactor;
        trackpadZoomRef.current.accumulatedZoom = newZoom;
        trackpadZoomRef.current.lastWheelTime = currentTime;

        // Clamp zoom based on camera capabilities
        const maxZoom =
          cameraCapabilities.zoom.max > 1 ? cameraCapabilities.zoom.max : 5;
        const clampedZoom = Math.max(
          cameraCapabilities.zoom.min,
          Math.min(newZoom, maxZoom)
        );

        // Apply zoom to camera only (not UI)
        if (Math.abs(clampedZoom - currentZoom) > 0.01) {
          applyZoom(clampedZoom);
        }
      }

      // Continue resetting browser zoom in case it tries to apply
      setTimeout(resetBrowserZoom, 0);
      setTimeout(resetBrowserZoom, 10);

      return false;
    }
  };

  // Reset capabilities when device changes
  useEffect(() => {
    if (!selectedDeviceId) {
      setCameraCapabilities({
        zoom: { min: 0.5, max: 1, step: 0.1 },
        focusMode: [],
        focusDistance: { min: 0, max: 1, step: 0.01 },
        exposureMode: [],
        exposureCompensation: { min: -2, max: 2, step: 0.1 },
        whiteBalanceMode: [],
        colorTemperature: null,
        torch: false,
      });
      setFlashEnabled(false);
      setZoomLevel(1);
      setCameraZoomLevel(1);
      setUiZoomLevel(1);
      setFocusMode("auto");
      setFocusDistance(0);
      setFocusPoint(null);
      setShowFocusIndicator(false);
      videoTrackRef.current = null;
      cameraFeedRef.current = null;
    }
  }, [selectedDeviceId]);

  // Cleanup focus indicator timeout
  useEffect(() => {
    return () => {
      if (focusIndicatorTimeoutRef.current) {
        clearTimeout(focusIndicatorTimeoutRef.current);
      }
    };
  }, []);

  // Prevent browser zoom on the entire popup (except for camera preview)
  useEffect(() => {
    const checkIfOnCameraPreview = (target) => {
      if (!target) return false;
      return !!(
        target.closest(".live-camera") ||
        target.closest(".webcam-feed") ||
        target.classList.contains("live-camera") ||
        target.classList.contains("webcam-feed") ||
        (target.tagName === "VIDEO" && target.closest(".live-camera"))
      );
    };

    // Aggressive prevention of browser zoom
    const preventBrowserZoom = (e) => {
      const target = e.target;
      const isOnCameraPreview = checkIfOnCameraPreview(target);

      // If it's a pinch gesture (Ctrl/Cmd + wheel) and NOT on camera preview, prevent it
      if ((e.ctrlKey || e.metaKey) && !isOnCameraPreview) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) {
          e.stopImmediatePropagation();
        }
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
        return false;
      }
    };

    // Prevent zoom on the entire document when popup is open
    const preventDocumentZoom = (e) => {
      const target = e.target;
      const isOnCameraPreview = checkIfOnCameraPreview(target);

      // If it's a pinch gesture (Ctrl/Cmd + wheel) and NOT on camera preview, prevent it
      if ((e.ctrlKey || e.metaKey) && !isOnCameraPreview) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) {
          e.stopImmediatePropagation();
        }
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
        return false;
      }
    };

    // Prevent zoom via keyboard shortcuts
    const preventKeyboardZoom = (e) => {
      // Prevent Ctrl/Cmd + Plus, Minus, 0 (zoom shortcuts)
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")
      ) {
        const target = e.target;
        const isOnCameraPreview = checkIfOnCameraPreview(target);
        if (!isOnCameraPreview) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    };

    // Add event listeners with capture phase to intercept early
    const overlay = document.querySelector(".webcam-overlay");
    const content = document.querySelector(".webcam-content");

    // Use capture phase to intercept before other handlers
    if (overlay) {
      overlay.addEventListener("wheel", preventBrowserZoom, {
        passive: false,
        capture: true,
      });
      overlay.addEventListener("keydown", preventKeyboardZoom, {
        passive: false,
        capture: true,
      });
    }
    if (content) {
      content.addEventListener("wheel", preventBrowserZoom, {
        passive: false,
        capture: true,
      });
      content.addEventListener("keydown", preventKeyboardZoom, {
        passive: false,
        capture: true,
      });
    }

    // Also prevent on document level with highest priority
    document.addEventListener("wheel", preventDocumentZoom, {
      passive: false,
      capture: true,
    });
    document.addEventListener("keydown", preventKeyboardZoom, {
      passive: false,
      capture: true,
    });

    // Also prevent gesture events (for touch devices)
    const preventGestures = (e) => {
      const target = e.target;
      const isOnCameraPreview = checkIfOnCameraPreview(target);
      if (!isOnCameraPreview) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("gesturestart", preventGestures, {
      passive: false,
      capture: true,
    });
    document.addEventListener("gesturechange", preventGestures, {
      passive: false,
      capture: true,
    });
    document.addEventListener("gestureend", preventGestures, {
      passive: false,
      capture: true,
    });

    return () => {
      if (overlay) {
        overlay.removeEventListener("wheel", preventBrowserZoom, {
          capture: true,
        });
        overlay.removeEventListener("keydown", preventKeyboardZoom, {
          capture: true,
        });
      }
      if (content) {
        content.removeEventListener("wheel", preventBrowserZoom, {
          capture: true,
        });
        content.removeEventListener("keydown", preventKeyboardZoom, {
          capture: true,
        });
      }
      document.removeEventListener("wheel", preventDocumentZoom, {
        capture: true,
      });
      document.removeEventListener("keydown", preventKeyboardZoom, {
        capture: true,
      });
      document.removeEventListener("gesturestart", preventGestures, {
        capture: true,
      });
      document.removeEventListener("gesturechange", preventGestures, {
        capture: true,
      });
      document.removeEventListener("gestureend", preventGestures, {
        capture: true,
      });
    };
  }, []);

  useEffect(() => {
    const getVideoDevices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera not supported in this browser");
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        mediaStreamRef.current = stream;
        setPermissionGranted(true);
        stream.getTracks().forEach((track) => track.stop());

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter((d) => d.kind === "videoinput");

        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          // Load saved camera preference
          const validation = validateCameraPreference(videoDevices);
          let deviceToSelect = videoDevices[0].deviceId; // Default to first device

          if (validation.isValid) {
            deviceToSelect = validation.deviceId;
          } else {
          }

          setSelectedDeviceId(deviceToSelect);
        }
      } catch (err) {
        setError(err.message || "Failed to access camera");
        setPermissionGranted(false);
      } finally {
        setIsLoading(false);
      }
    };

    getVideoDevices();
  }, []);

  // Listen for device changes (e.g., when devices are added/removed)
  useEffect(() => {
    const handleDeviceChange = () => {
      // Refresh device list when devices change
      navigator.mediaDevices.enumerateDevices().then((allDevices) => {
        const videoDevices = allDevices.filter((d) => d.kind === "videoinput");
        setDevices(videoDevices);

        // Check if current selected device still exists
        if (
          selectedDeviceId &&
          !videoDevices.some((d) => d.deviceId === selectedDeviceId)
        ) {
          // Current device no longer available, switch to first available
          if (videoDevices.length > 0) {
            setSelectedDeviceId(videoDevices[0].deviceId);
            saveCameraPreferences(
              videoDevices[0].deviceId,
              videoDevices[0].label || "Camera 1"
            );
          }
        }
      });
    };

    // Check if addEventListener is available on mediaDevices
    if (
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.addEventListener === "function"
    ) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange
      );

      return () => {
        if (
          navigator.mediaDevices &&
          typeof navigator.mediaDevices.removeEventListener === "function"
        ) {
          navigator.mediaDevices.removeEventListener(
            "devicechange",
            handleDeviceChange
          );
        }
      };
    }
  }, [selectedDeviceId]);

  // Video constraints for the webcam (use useMemo to ensure new object reference when dependencies change)
  const videoConstraints = useMemo(() => {
    const constraints = {
      width: 1280,
      height: 720,
      deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
    };

    // Add zoom to constraints if supported (optical zoom is better than cropping)
    if (cameraCapabilities.zoom.max > 1) {
      constraints.zoom = zoomLevel;
    }

    return constraints;
  }, [selectedDeviceId, cameraCapabilities.zoom.max, zoomLevel]);

  // Helper function to apply Gaussian blur (for unsharp mask)
  // Optimized for performance with limited kernel size
  const applyGaussianBlur = (imageData, radius) => {
    const data = new Uint8ClampedArray(imageData.data);
    const width = imageData.width;
    const height = imageData.height;
    const output = new Uint8ClampedArray(data.length);
    
    // Limit radius for performance (max 3 pixels)
    const limitedRadius = Math.min(radius, 3);
    
    // Create Gaussian kernel (limited size for performance)
    const kernelSize = Math.min(Math.ceil(limitedRadius * 2) * 2 + 1, 15); // Max 15x15 kernel
    const kernel = [];
    const sigma = limitedRadius / 3;
    let sum = 0;
    
    for (let i = 0; i < kernelSize; i++) {
      const x = i - Math.floor(kernelSize / 2);
      const value = Math.exp(-(x * x) / (2 * sigma * sigma));
      kernel[i] = value;
      sum += value;
    }
    
    // Normalize kernel
    for (let i = 0; i < kernelSize; i++) {
      kernel[i] /= sum;
    }
    
    // Apply horizontal blur
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0, g = 0, b = 0, a = 0;
        const halfKernel = Math.floor(kernelSize / 2);
        for (let k = 0; k < kernelSize; k++) {
          const px = Math.max(0, Math.min(width - 1, x + k - halfKernel));
          const idx = (y * width + px) * 4;
          const weight = kernel[k];
          r += data[idx] * weight;
          g += data[idx + 1] * weight;
          b += data[idx + 2] * weight;
          a += data[idx + 3] * weight;
        }
        const outIdx = (y * width + x) * 4;
        output[outIdx] = Math.round(r);
        output[outIdx + 1] = Math.round(g);
        output[outIdx + 2] = Math.round(b);
        output[outIdx + 3] = Math.round(a);
      }
    }
    
    // Apply vertical blur
    const temp = new Uint8ClampedArray(output);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0, g = 0, b = 0, a = 0;
        const halfKernel = Math.floor(kernelSize / 2);
        for (let k = 0; k < kernelSize; k++) {
          const py = Math.max(0, Math.min(height - 1, y + k - halfKernel));
          const idx = (py * width + x) * 4;
          const weight = kernel[k];
          r += temp[idx] * weight;
          g += temp[idx + 1] * weight;
          b += temp[idx + 2] * weight;
          a += temp[idx + 3] * weight;
        }
        const outIdx = (y * width + x) * 4;
        output[outIdx] = Math.round(r);
        output[outIdx + 1] = Math.round(g);
        output[outIdx + 2] = Math.round(b);
        output[outIdx + 3] = Math.round(a);
      }
    }
    
    return new ImageData(output, width, height);
  };

  // Image enhancement function focused ONLY on clarity/sharpening - preserves colors exactly
  // Uses very aggressive multi-pass sharpening for maximum clarity
  const applyAdvancedEnhancement = (
    imageDataUrl,
    focusPoint = null,
    options = {}
  ) => {
    const {
      sharpnessAmount = 12.0, // Very high = maximum aggressive sharpening
      sharpnessRadius = 0.5, // Very tight blur radius for maximum detail
      enhancementRadius = 0.4, // Larger radius (40% of image) for better coverage
      passes = 3, // Multiple passes for maximum cumulative effect
    } = options;

    
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw original image
          ctx.drawImage(img, 0, 0);
          let currentImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          // Calculate enhancement area
          let focusX = canvas.width / 2;
          let focusY = canvas.height / 2;
          let maxRadius = Math.max(canvas.width, canvas.height) * enhancementRadius;
          let maxRadiusSquared = maxRadius * maxRadius;
          let applyLocalized = false;

          if (focusPoint && typeof focusPoint === 'object' && 'x' in focusPoint && 'y' in focusPoint) {
            focusX = focusPoint.x * canvas.width;
            focusY = focusPoint.y * canvas.height;
            applyLocalized = true;
          } else {
          }

          // Apply multiple passes for stronger effect
          let totalPixelsEnhanced = 0;
          let maxEnhancementApplied = 0;
          
          for (let pass = 0; pass < passes; pass++) {
            const currentData = currentImageData.data;
            
            // Create blurred version for unsharp mask
            const blurredImageData = applyGaussianBlur(currentImageData, sharpnessRadius);
            const blurredData = blurredImageData.data;

            // Create output image data
            const output = new Uint8ClampedArray(currentData.length);
            let pixelsInRadius = 0;
            let maxFactorThisPass = 0;

            // Process each pixel - ONLY sharpening, NO color changes
            for (let y = 0; y < canvas.height; y++) {
              for (let x = 0; x < canvas.width; x++) {
                const idx = (y * canvas.width + x) * 4;
                
                // Calculate enhancement factor based on distance from focus point
                let enhancementFactor = 1.0;
                if (applyLocalized) {
                  const dx = x - focusX;
                  const dy = y - focusY;
                  const distanceSquared = dx * dx + dy * dy;
                  
                  if (distanceSquared < maxRadiusSquared) {
                    pixelsInRadius++;
                    const distance = Math.sqrt(distanceSquared);
                    const normalizedDistance = distance / maxRadius;
                    // Less aggressive falloff - ensures more area gets strong enhancement
                    // Use power curve for smoother but still strong enhancement
                    enhancementFactor = Math.max(0, Math.pow(1 - normalizedDistance, 0.6)); // Power curve for better coverage
                    if (enhancementFactor > maxFactorThisPass) {
                      maxFactorThisPass = enhancementFactor;
                    }
                  } else {
                    enhancementFactor = 0;
                  }
                }

                // Get current pixel values (from previous pass or original)
                const r = currentData[idx];
                const g = currentData[idx + 1];
                const b = currentData[idx + 2];
                const a = currentData[idx + 3];

                // Get blurred pixel values for unsharp mask
                const blurR = blurredData[idx];
                const blurG = blurredData[idx + 1];
                const blurB = blurredData[idx + 2];

                // Apply aggressive unsharp mask (sharpening) - preserves color ratios
                // Formula: original + (original - blurred) * amount
                // Multiple passes compound the effect
                const sharpR = r + (r - blurR) * sharpnessAmount * enhancementFactor;
                const sharpG = g + (g - blurG) * sharpnessAmount * enhancementFactor;
                const sharpB = b + (b - blurB) * sharpnessAmount * enhancementFactor;

                // Clamp values to valid range - colors remain exactly the same, just sharper
                output[idx] = Math.min(255, Math.max(0, Math.round(sharpR)));
                output[idx + 1] = Math.min(255, Math.max(0, Math.round(sharpG)));
                output[idx + 2] = Math.min(255, Math.max(0, Math.round(sharpB)));
                output[idx + 3] = a; // Preserve alpha
              }
            }

            if (applyLocalized) {
              totalPixelsEnhanced = pixelsInRadius;
              maxEnhancementApplied = maxFactorThisPass;
            }

            // Update current image data for next pass
            currentImageData = new ImageData(output, canvas.width, canvas.height);
          }
          

          // Put final enhanced image data back
          ctx.putImageData(currentImageData, 0, 0);

          // Convert to data URL
          const enhancedDataUrl = canvas.toDataURL("image/png");
          resolve(enhancedDataUrl);
        } catch (error) {
          console.error("[ENHANCEMENT] Error applying enhancement:", error);
          resolve(imageDataUrl); // Fallback to original
        }
      };
      img.onerror = () => {
        console.error("[ENHANCEMENT] Error loading image");
        resolve(imageDataUrl); // Fallback to original
      };
      img.src = imageDataUrl;
    });
  };

  // Legacy function name for backward compatibility - now only does sharpening
  const applyBrightnessAdjustment = (
    imageDataUrl,
    brightnessBoost = 0.5,
    focusPoint = null
  ) => {
    // Only apply sharpening - no brightness/contrast changes to preserve colors
    return applyAdvancedEnhancement(imageDataUrl, focusPoint, {
      sharpnessAmount: 10.0, // Very aggressive sharpening for maximum clarity
      sharpnessRadius: 0.6, // Tighter radius for sharper edges
      enhancementRadius: 0.35, // 35% radius for better coverage
      passes: 2, // Multiple passes for stronger effect
    });
  };

  const captureImage = async () => {

    if (!webcamRef.current) {
      return;
    }

    // Add this debug to check if focus point exists
    if (!focusPoint) {
      // Still continue with capture but without enhancement
    } else {
    }

    const videoElement = cameraFeedRef.current || webcamRef.current?.video;
    if (!videoElement) {
      return;
    }

    // Get the base screenshot
    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) {
      return;
    }

    // Check if we need to handle CSS zoom cropping
    const needsCropZoom = cameraCapabilities.zoom.max <= 1 && uiZoomLevel > 1;

    // If using optical zoom, the screenshot should already be zoomed
    // If using CSS zoom, we need to crop to show only the visible zoomed portion
    if (needsCropZoom) {
      // Create a canvas to crop the zoomed region from the screenshot
      const img = new Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Use the screenshot dimensions (which should match video dimensions)
          const imgWidth = img.width;
          const imgHeight = img.height;

          // Calculate the crop region (centered, showing only 1/zoomLevel of the original)
          // When zoomed to 2x, we want to show only 1/2 of the width and height (the center portion)
          const zoom = uiZoomLevel;
          const cropWidth = imgWidth / zoom;
          const cropHeight = imgHeight / zoom;
          const cropX = (imgWidth - cropWidth) / 2;
          const cropY = (imgHeight - cropHeight) / 2;

          // Set canvas size to match the cropped region (or scale it up to original size for better quality)
          // We'll keep it at cropped size to maintain the zoom effect
          canvas.width = cropWidth;
          canvas.height = cropHeight;

          // Draw the cropped portion from the center of the image
          ctx.drawImage(
            img,
            cropX,
            cropY,
            cropWidth,
            cropHeight, // Source: crop region from screenshot
            0,
            0,
            cropWidth,
            cropHeight // Destination: full canvas
          );

          // Convert to data URL
          let croppedDataUrl = canvas.toDataURL("image/png");

          // Aggressive clarity enhancement - most webcams don't support hardware tap-to-focus
          
          if (focusPoint && typeof focusPoint === 'object' && 'x' in focusPoint && 'y' in focusPoint) {
            
            // CRITICAL: Adjust focus point coordinates for cropped image!
            // The focus point is in normalized coordinates (0-1) relative to the FULL image
            // But we cropped the image, so we need to convert to cropped coordinates
            const originalFocusX = focusPoint.x * imgWidth;
            const originalFocusY = focusPoint.y * imgHeight;
            
            // Convert to cropped image coordinates (normalized 0-1 relative to cropped image)
            const adjustedFocusX = (originalFocusX - cropX) / cropWidth;
            const adjustedFocusY = (originalFocusY - cropY) / cropHeight;
            
            // Clamp to valid range (0-1)
            const adjustedFocusPoint = {
              x: Math.max(0, Math.min(1, adjustedFocusX)),
              y: Math.max(0, Math.min(1, adjustedFocusY))
            };
            
            
            const zoomFactor = Math.max(1, uiZoomLevel);
            const sharpnessMultiplier = Math.min(3.0, 1 + (zoomFactor - 1) * 0.4);
            
            // Very aggressive sharpening for maximum clarity - colors preserved
            const beforeLength = croppedDataUrl.length;
            croppedDataUrl = await applyAdvancedEnhancement(
              croppedDataUrl,
              adjustedFocusPoint, // Use adjusted coordinates!
              {
                sharpnessAmount: 12.0 * sharpnessMultiplier, // Very aggressive sharpening
                sharpnessRadius: 0.5, // Very tight radius for maximum detail
                enhancementRadius: 0.4, // 40% radius for better coverage
                passes: 3, // Multiple passes for maximum effect
              }
            );
          } else {
          }

          setCaptured(croppedDataUrl);
          
          // Clear focus point after capture for next shot
          setFocusPoint(null);
          setShowFocusIndicator(false);
        } catch (error) {
          console.error("[WEBCAM DEBUG] Error cropping zoomed image:", error);
          // Fallback to original screenshot
          setCaptured(screenshot);
        }
      };
      img.onerror = (error) => {
        console.error(
          "[WEBCAM DEBUG] Error loading screenshot for crop:",
          error
        );
        // Fallback to original screenshot
        setCaptured(screenshot);
      };
      img.src = screenshot;
    } else {
      // Optical zoom or no zoom - screenshot should already have correct zoom applied
      // For optical zoom, the camera itself is zoomed, so getScreenshot() captures the zoomed view
      let finalScreenshot = screenshot;

      // Aggressive clarity enhancement - most webcams don't support hardware tap-to-focus
      
      if (focusPoint && typeof focusPoint === 'object' && 'x' in focusPoint && 'y' in focusPoint) {
        const currentZoom = cameraCapabilities.zoom.max > 1 ? zoomLevel : uiZoomLevel;
        const zoomFactor = Math.max(1, currentZoom);
        const sharpnessMultiplier = Math.min(3.0, 1 + (zoomFactor - 1) * 0.4);
        
        // Very aggressive sharpening for maximum clarity - colors preserved
        const beforeLength = finalScreenshot.length;
        finalScreenshot = await applyAdvancedEnhancement(
          finalScreenshot,
          focusPoint,
          {
            sharpnessAmount: 12.0 * sharpnessMultiplier, // Very aggressive sharpening
            sharpnessRadius: 0.5, // Very tight radius for maximum detail
            enhancementRadius: 0.4, // 40% radius for better coverage
            passes: 3, // Multiple passes for maximum effect
          }
        );
      } else {
      }

      setCaptured(finalScreenshot);
      
      // Clear focus point after capture for next shot
      setFocusPoint(null);
      setShowFocusIndicator(false);
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
    });
    mediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: "video/webm",
    });

    const chunks = [];
    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setCapturedVideo(url);
    };

    mediaRecorderRef.current.start();
    setRecordedChunks(chunks);
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const saveVideo = () => {
    if (capturedVideo) {
      addVideo(capturedVideo);
      setShowWebcamPopup(false);
    }
  };

  const saveImage = () => {
    if (captured) {
      try {
        setCapturedImage(captured);
        addImage(captured);
        setShowWebcamPopup(false);
      } catch (error) {
        const errorMessage = error?.message || "Failed to add image from webcam";
        if (showErrorSnackbar) {
          showErrorSnackbar(errorMessage);
        } else {
          alert(errorMessage);
        }
      }
    }
  };

  // Prevent body zoom when popup is open - ULTRA AGGRESSIVE PREVENTION
  useEffect(() => {
    const originalBodyZoom = document.body.style.zoom;
    const originalHtmlZoom = document.documentElement.style.zoom;
    const originalBodyTransform = document.body.style.transform;
    const originalHtmlTransform = document.documentElement.style.transform;
    const originalBodyScale = document.body.style.scale;
    const originalHtmlScale = document.documentElement.style.scale;

    // Store original scrollbar visibility
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    // Store original viewport meta if it exists
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewportMeta
      ? viewportMeta.getAttribute("content")
      : null;

    // Hide scrollbars when popup is open
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // Hide custom scrollbars if they exist
    const scrollbars = document.querySelectorAll('[data-scrollbar-track], .custom-scrollbar-track');
    
    const originalScrollbarDisplays = new Map();
    scrollbars.forEach((scrollbar) => {
      originalScrollbarDisplays.set(scrollbar, scrollbar.style.display);
      scrollbar.style.display = "none";
    });

    // Force viewport to prevent zoom
    if (viewportMeta) {
      viewportMeta.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      );
    }

    // Reset any zoom on body/html and all popup elements
    const resetZoom = () => {
      // Check computed zoom and reset if not 1
      const bodyComputedZoom = window.getComputedStyle(document.body).zoom;
      const htmlComputedZoom = window.getComputedStyle(
        document.documentElement
      ).zoom;

      // Log if zoom is detected
      if (bodyComputedZoom !== "1" && bodyComputedZoom !== "normal") {
        console.warn("[WEBCAM DEBUG] Body zoom detected:", bodyComputedZoom);
        document.body.style.zoom = "1";
      }
      if (htmlComputedZoom !== "1" && htmlComputedZoom !== "normal") {
        console.warn("[WEBCAM DEBUG] HTML zoom detected:", htmlComputedZoom);
        document.documentElement.style.zoom = "1";
      }

      document.body.style.zoom = "1";
      document.documentElement.style.zoom = "1";
      document.body.style.transform = "scale(1)";
      document.documentElement.style.transform = "scale(1)";
      document.body.style.scale = "1";
      document.documentElement.style.scale = "1";

      // Reset overlay and content
      const overlay = document.querySelector(".webcam-overlay");
      const content = document.querySelector(".webcam-content");
      const preview = document.querySelector(".webcam-preview");

      if (overlay) {
        const overlayZoom = window.getComputedStyle(overlay).zoom;
        if (overlayZoom !== "1" && overlayZoom !== "normal") {
          overlay.style.zoom = "1";
        }
        overlay.style.zoom = "1";
        overlay.style.transform = "scale(1)";
        overlay.style.scale = "1";
      }
      if (content) {
        const contentZoom = window.getComputedStyle(content).zoom;
        if (contentZoom !== "1" && contentZoom !== "normal") {
          content.style.zoom = "1";
        }
        content.style.zoom = "1";
        content.style.transform = "scale(1)";
        content.style.scale = "1";
      }
      if (preview) {
        const previewZoom = window.getComputedStyle(preview).zoom;
        if (previewZoom !== "1" && previewZoom !== "normal") {
          preview.style.zoom = "1";
        }
        preview.style.zoom = "1";
        preview.style.transform = "scale(1)";
        preview.style.scale = "1";
      }

      // Reset all floating UI elements
      const floatingElements = document.querySelectorAll(
        ".webcam-floating-close, .webcam-floating-device-select, .webcam-floating-camera-controls, .webcam-floating-controls"
      );
      floatingElements.forEach((el) => {
        if (el) {
          const elZoom = window.getComputedStyle(el).zoom;
          if (elZoom !== "1" && elZoom !== "normal") {
            el.style.zoom = "1";
          }
          el.style.zoom = "1";
          el.style.scale = "1";
          // Preserve translateX for centered elements but reset scale
          const currentTransform = window.getComputedStyle(el).transform;
          if (currentTransform && currentTransform !== "none") {
            // Extract translateX if present
            const match = currentTransform.match(/translateX\(([^)]+)\)/);
            if (match) {
              el.style.transform = `translateX(${match[1]}) scale(1)`;
            } else {
              el.style.transform = "scale(1)";
            }
          } else {
            el.style.transform = "scale(1)";
          }
        }
      });
    };

    resetZoom();

    // Add class to body to prevent zoom
    document.body.classList.add("webcam-popup-open");
    document.documentElement.classList.add("webcam-popup-open");


    // Continuously reset zoom (in case browser tries to apply it) - VERY FAST
    let resetCount = 0;
    const zoomResetInterval = setInterval(() => {
      resetZoom();
      resetCount++;
      // Log every 100 resets (every ~1 second) to avoid spam
      if (resetCount % 100 === 0) {
        const bodyZoom = window.getComputedStyle(document.body).zoom;
        const htmlZoom = window.getComputedStyle(document.documentElement).zoom;
        if (
          (bodyZoom !== "1" && bodyZoom !== "normal") ||
          (htmlZoom !== "1" && htmlZoom !== "normal")
        ) {
          console.warn(
            "[WEBCAM DEBUG] Zoom detected during continuous reset:",
            {
              bodyZoom,
              htmlZoom,
              resetCount,
            }
          );
        }
      }
    }, 10); // Every 10ms

    // Use MutationObserver to detect any style changes and reset immediately
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          const target = mutation.target;
          // If zoom or transform changed on body/html/overlay/content, reset it
          if (
            target === document.body ||
            target === document.documentElement ||
            target.classList?.contains("webcam-overlay") ||
            target.classList?.contains("webcam-content") ||
            target.classList?.contains("webcam-preview")
          ) {
            const newStyle = target.getAttribute("style") || "";
            if (
              newStyle.includes("zoom") ||
              newStyle.includes("scale") ||
              newStyle.includes("transform")
            ) {
              console.warn(
                "[WEBCAM DEBUG] MutationObserver detected style change:",
                {
                  target: target.className || target.tagName,
                  style: newStyle,
                }
              );
              resetZoom();
            }
          }
        }
      });
    });

    // Observe body and html for style changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // Also prevent on every wheel event at window/document level
    const preventAllZoom = (e) => {
      const target = e.target;
      const isOnCameraPreview =
        target &&
        (target.closest(".live-camera") ||
          target.closest(".webcam-feed") ||
          target.closest(".camera-feed-wrapper") ||
          target.classList.contains("live-camera") ||
          target.classList.contains("webcam-feed") ||
          target.classList.contains("camera-feed-wrapper") ||
          (target.tagName === "VIDEO" && target.closest(".live-camera")));

      // Check if this is a pinch gesture
      const isPinchGesture =
        e.ctrlKey ||
        e.metaKey ||
        (e.deltaMode === 0 &&
          Math.abs(e.deltaY) < 100 &&
          Math.abs(e.deltaX) < 100);

      // If it's a pinch gesture, ALWAYS prevent browser zoom
      if (isPinchGesture) {
        e.preventDefault();

        // Reset browser zoom immediately
        resetZoom();

        // If NOT on camera preview, stop propagation completely
        if (!isOnCameraPreview) {
          e.stopPropagation();
          if (e.stopImmediatePropagation) {
            e.stopImmediatePropagation();
          }
          if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
            e.nativeEvent.stopImmediatePropagation();
          }
          return false;
        }
        // If on camera preview, don't stop propagation - let handleWheel handle camera zoom
        // But still prevent default browser zoom behavior
      }
    };

    // Add listeners at multiple levels with capture phase
    window.addEventListener("wheel", preventAllZoom, {
      passive: false,
      capture: true,
    });
    document.addEventListener("wheel", preventAllZoom, {
      passive: false,
      capture: true,
    });
    document.body.addEventListener("wheel", preventAllZoom, {
      passive: false,
      capture: true,
    });

    return () => {
      clearInterval(zoomResetInterval);
      observer.disconnect();
      window.removeEventListener("wheel", preventAllZoom, { capture: true });
      document.removeEventListener("wheel", preventAllZoom, { capture: true });
      document.body.removeEventListener("wheel", preventAllZoom, {
        capture: true,
      });
      document.body.style.zoom = originalBodyZoom || "";
      document.documentElement.style.zoom = originalHtmlZoom || "";
      document.body.style.transform = originalBodyTransform || "";
      document.documentElement.style.transform = originalHtmlTransform || "";
      document.body.style.scale = originalBodyScale || "";
      document.documentElement.style.scale = originalHtmlScale || "";
      document.body.style.overflow = originalBodyOverflow || "";
      document.documentElement.style.overflow = originalHtmlOverflow || "";
      
      // Restore custom scrollbars
      originalScrollbarDisplays.forEach((display, scrollbar) => {
        scrollbar.style.display = display;
      });
      
      document.body.classList.remove("webcam-popup-open");
      document.documentElement.classList.remove("webcam-popup-open");
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute("content", originalViewport);
      }
    };
  }, []);

  // Helper to check if event is on camera preview
  const isOnCameraPreview = (target) => {
    if (!target) return false;
    return !!(
      target.closest(".live-camera") ||
      target.closest(".webcam-feed") ||
      target.classList.contains("live-camera") ||
      target.classList.contains("webcam-feed") ||
      (target.tagName === "VIDEO" && target.closest(".live-camera"))
    );
  };

  // Prevent zoom on overlay
  const handleOverlayWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (!isOnCameraPreview(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) {
          e.stopImmediatePropagation();
        }
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
      }
    }
  };

  // Prevent zoom on content
  const handleContentWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (!isOnCameraPreview(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) {
          e.stopImmediatePropagation();
        }
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
      }
    }
  };

  return (
    <div
      className="webcam-overlay"
      onWheel={handleOverlayWheel}
      onWheelCapture={handleOverlayWheel}
    >
      <div
        className="webcam-content"
        onWheel={handleContentWheel}
        onWheelCapture={handleContentWheel}
      >
        {/* Main Preview Area (Fullscreen) */}
        <div
          className="webcam-preview"
          onClick={(e) => {
            // Only handle click if it's not on an interactive element
            const target = e.target;
            const isInteractive =
              target.closest("button") ||
              target.closest("input") ||
              target.closest("select") ||
              target.closest(".webcam-floating-controls") ||
              target.closest(".webcam-zoom-control") ||
              target.closest(".webcam-top-controls");
            if (!isInteractive) {
              handleTapToFocus(e);
            }
          }}
          onDoubleClick={(e) => {
            // Clear focus point on double click
            const target = e.target;
            const isInteractive =
              target.closest("button") ||
              target.closest("input") ||
              target.closest("select") ||
              target.closest(".webcam-floating-controls") ||
              target.closest(".webcam-zoom-control") ||
              target.closest(".webcam-top-controls");
            if (!isInteractive) {
              setFocusPoint(null);
              setShowFocusIndicator(false);
            }
          }}
        >
          {permissionGranted && devices.length > 0 && (
            <>
              <div className="webcam-floating-device-select">
                <label
                  htmlFor="webcam-device-select-input"
                  className="webcam-device-label"
                >
                  Camera
                </label>
                <select
                  id="webcam-device-select-input"
                  className="webcam-device-dropdown"
                  value={selectedDeviceId || ""}
                  onChange={(event) => handleDeviceChange(event.target.value)}
                >
                  {devices.map((device, index) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {getDeviceLabel(device, index)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Focus Controls (Zoom moved to above capture button) */}
              {cameraCapabilities.focusMode.length > 0 && (
                <div className="webcam-floating-camera-controls">
                  <div className="webcam-control-group">
                    <label className="webcam-control-label">Focus</label>
                    <select
                      value={focusMode}
                      onChange={handleFocusModeChange}
                      className="webcam-control-select"
                    >
                      {cameraCapabilities.focusMode.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </option>
                      ))}
                    </select>
                    {focusMode === "manual" &&
                      cameraCapabilities.focusDistance.max > 0 && (
                        <div className="webcam-focus-manual">
                          <label className="webcam-control-label">
                            Distance: {focusDistance.toFixed(2)}
                          </label>
                          <input
                            type="range"
                            min={cameraCapabilities.focusDistance.min}
                            max={cameraCapabilities.focusDistance.max}
                            step={cameraCapabilities.focusDistance.step}
                            value={focusDistance}
                            onChange={handleFocusDistanceChange}
                            className="webcam-control-slider"
                          />
                        </div>
                      )}
                  </div>
                </div>
              )}
            </>
          )}
          {isLoading ? (
            <div className="webcam-loading">
              <div className="webcam-spinner"></div>
              <p>Initializing camera...</p>
            </div>
          ) : error ? (
            <div></div>
          ) : captured ? (
            <div className="captured-content">
              <img src={captured} alt="Captured" className="captured-image" />
              <div className="captured-overlay">
                <span className="captured-label">Image Captured</span>
              </div>
            </div>
          ) : capturedVideo ? (
            <div className="captured-content">
              <video src={capturedVideo} controls className="captured-video" />
              <div className="captured-overlay">
                <span className="captured-label">Video Recorded</span>
              </div>
            </div>
          ) : permissionGranted ? (
            <div
              ref={liveCameraRef}
              className="live-camera"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              onWheel={handleWheel}
              onWheelCapture={handleWheel}
              onClick={handleTapToFocus}
              style={{
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                cursor: "crosshair",
              }}
            >
              <div
                className="camera-feed-wrapper"
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // Isolate this container - no zoom should affect it
                  transform: "scale(1) !important",
                  zoom: "1 !important",
                }}
              >
                <Webcam
                  ref={(ref) => {
                    webcamRef.current = ref;
                    // Get the actual video element
                    if (ref && ref.video) {
                      cameraFeedRef.current = ref.video;
                      // Apply current zoom if UI zoom is set (using helper function)
                      if (
                        cameraCapabilities.zoom.max <= 1 &&
                        uiZoomLevel !== 1
                      ) {
                        applyZoomToVideoElement(uiZoomLevel);
                      } else if (cameraCapabilities.zoom.max > 1) {
                        // Clear CSS transform when using optical zoom
                        applyZoomToVideoElement(1);
                      }
                    }
                  }}
                  screenshotFormat="image/png"
                  className="webcam-feed"
                  videoConstraints={videoConstraints}
                  onUserMedia={handleUserMedia}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    // Zoom is applied via direct style manipulation in applyZoom and useEffect
                    // Note: Visual enhancement is now applied only to the tap point area, not the entire preview
                  }}
                />
              </div>
              <div className="camera-overlay-indicator">
                {/* <span>Live Camera Feed</span> */}
              </div>
              {/* Focus Indicator with Localized Enhancement */}
              {showFocusIndicator && focusPoint && (
                <>
                  <div
                    className={`focus-indicator ${showFocusIndicator ? 'focusing' : ''}`}
                    style={{
                      left: `${focusPoint.x * 100}%`,
                      top: `${focusPoint.y * 100}%`,
                    }}
                  >
                    <div className="focus-indicator-ring"></div>
                    <div className="focus-indicator-crosshair"></div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="webcam-permission">
              <div className="permission-icon">🔒</div>
              <h4>Camera Permission Required</h4>
              <p>Please allow camera access to use this feature.</p>
            </div>
          )}

          {/* Floating Controls - Top Right */}
          <div className="webcam-top-controls">
            {/* Flash/Torch Toggle Button - Always visible */}
            <div className="webcam-flash-btn-wrapper">
              <button
                className={`webcam-flash-btn ${flashEnabled ? "active" : ""} ${
                  !cameraCapabilities.torch ? "disabled" : ""
                }`}
                onClick={toggleFlash}
                disabled={!cameraCapabilities.torch}
                aria-label={
                  !cameraCapabilities.torch
                    ? "Flash not supported on this device"
                    : flashEnabled
                    ? "Turn off flash"
                    : "Turn on flash"
                }
                title={
                  !cameraCapabilities.torch
                    ? "Flash not supported on this device"
                    : flashEnabled
                    ? "Turn off flash"
                    : "Turn on flash"
                }
              >
                {flashEnabled ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
                      opacity="0.5"
                    ></path>
                  </svg>
                )}
              </button>
              {!cameraCapabilities.torch && (
                <div className="webcam-flash-tooltip">
                  Flash not supported on this device
                </div>
              )}
            </div>
            {/* Close Button */}
            <button
              className="webcam-floating-close"
              onClick={() => setShowWebcamPopup(false)}
              aria-label="Close camera"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Floating Controls */}
          {!captured && !capturedVideo ? (
            permissionGranted && (
              <div className="webcam-floating-controls-wrapper">
                {/* Zoom Control - Above capture button (like smartphone camera apps) */}
                <div className="webcam-zoom-control">
                  {/* Zoom Preset Buttons (like smartphone camera apps) */}
                  <div className="webcam-zoom-presets">
                    {zoomPresets.map((preset) => {
                      const maxZoom =
                        cameraCapabilities.zoom.max > 1
                          ? cameraCapabilities.zoom.max
                          : 5;
                      const minZoom = cameraCapabilities.zoom.min || 0.5;

                      // Only show presets within camera's zoom range
                      if (preset < minZoom || preset > maxZoom) return null;

                      return (
                        <button
                          key={preset}
                          className={`webcam-zoom-preset-btn ${
                            Math.abs(zoomLevel - preset) < 0.1 ? "active" : ""
                          }`}
                          onClick={() => handleZoomPreset(preset)}
                          aria-label={`Zoom ${preset}x`}
                        >
                          {preset}
                        </button>
                      );
                    })}
                  </div>
                  <div className="webcam-zoom-slider-container">
                    <span className="webcam-zoom-label">
                      {Math.max(1, zoomLevel).toFixed(1)}x
                    </span>
                    <input
                      type="range"
                      min={1}
                      max={
                        cameraCapabilities.zoom.max > 1
                          ? cameraCapabilities.zoom.max
                          : 5
                      }
                      step={cameraCapabilities.zoom.step || 0.1}
                      value={Math.max(1, zoomLevel)}
                      onChange={handleZoomChange}
                      className="webcam-zoom-slider"
                    />
                  </div>
                </div>
                <div className="webcam-floating-controls">
                  {!isRecording && (
                    <button
                      className="webcam-btn webcam-btn-capture"
                      onClick={captureImage}
                      aria-label="Capture photo"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      <span>Capture</span>
                    </button>
                  )}

                  {/* {!isRecording ? (
                  <button
                    className="webcam-btn webcam-btn-record"
                    onClick={startRecording}
                    aria-label="Start recording video"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <circle
                        cx="12"
                        cy="12"
                        r="6"
                        fill="currentColor"
                      ></circle>
                    </svg>
                    <span>Record</span>
                  </button>
                ) : (
                  <button
                    className="webcam-btn webcam-btn-stop"
                    onClick={stopRecording}
                    aria-label="Stop recording video"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="6" y="6" width="12" height="12"></rect>
                    </svg>
                    <span>Stop</span>
                  </button>
                )} */}
                </div>
              </div>
            )
          ) : captured ? (
            <div className="webcam-floating-controls-wrapper">
              <div className="webcam-floating-controls">
                <button
                  className="webcam-btn webcam-btn-secondary"
                  onClick={() => setCaptured(null)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                    <path d="M12 7v5l3 3"></path>
                  </svg>
                  Retake
                </button>
                <button
                  className="webcam-btn webcam-btn-primary"
                  onClick={saveImage}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17,21 17,13 7,13 7,21"></polyline>
                    <polyline points="7,3 7,8 15,8"></polyline>
                  </svg>
                  Add to Canvas
                </button>
              </div>
            </div>
          ) : (
            <div className="webcam-floating-controls-wrapper">
              <div className="webcam-floating-controls">
                <button
                  className="webcam-btn webcam-btn-secondary"
                  onClick={() => setCapturedVideo(null)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                    <path d="M12 7v5l3 3"></path>
                  </svg>
                  Re-record
                </button>
                <button
                  className="webcam-btn webcam-btn-primary"
                  onClick={saveVideo}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17,21 17,13 7,13 7,21"></polyline>
                    <polyline points="7,3 7,8 15,8"></polyline>
                  </svg>
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

WebcamPopup.propTypes = {
  setShowWebcamPopup: PropTypes.func.isRequired,
  setCapturedImage: PropTypes.func.isRequired,
  addImage: PropTypes.func.isRequired,
  addVideo: PropTypes.func.isRequired,
  showErrorSnackbar: PropTypes.func,
};

export default WebcamPopup;
